import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import GrassIdentifier from "@/pages/GrassIdentifier";
import Pricing from "@/pages/Pricing";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Settings from "@/pages/Settings";
import ConfirmSubscription from "@/pages/ConfirmSubscription";
import Unsubscribe from "@/pages/Unsubscribe";
import ConsumerDashboard from "@/pages/ConsumerDashboard";
import TreeCreate from "@/pages/TreeCreate";
import TreeDetail from "@/pages/TreeDetail";
import ProviderDashboard from "@/pages/ProviderDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ChangePassword from "@/pages/ChangePassword";
import { PreviewBanner } from "@/components/PreviewBanner";

const landing = (role) => (role === "admin" ? "/admin" : role === "provider" ? "/provider" : "/dashboard");

const Growing = () => <div className="min-h-screen flex items-center justify-center text-emerald-900 font-serif text-2xl">Growing…</div>;

const AccessDenied = () => (
  <div data-testid="access-denied" className="min-h-screen flex flex-col items-center justify-center bg-stone-50 px-6 text-center">
    <div className="font-serif text-7xl text-emerald-900">403</div>
    <h1 className="mt-4 font-serif text-3xl text-emerald-950">Access denied</h1>
    <p className="mt-3 text-stone-500 max-w-md">You don't have permission to view the admin dashboard. This area is restricted to platform administrators.</p>
    <a href="/" data-testid="access-denied-home" className="mt-7 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white px-6 py-2.5 text-sm transition-colors">Back to home</a>
  </div>
);

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Growing />;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.must_change_password && location.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={landing(user.role)} replace />;
  return children;
};

// Admin routes: redirect to login when logged out, show a 403 page for non-admins.
const AdminProtected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Growing />;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "admin") return <AccessDenied />;
  return children;
};

function RoutedApp() {
  const location = useLocation();
  // Process the Google OAuth callback synchronously before any protected route runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <PreviewBanner />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/grass" element={<GrassIdentifier />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:id" element={<BlogPost />} />
      <Route path="/confirm-subscription" element={<ConfirmSubscription />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/settings" element={<Protected roles={["consumer", "provider", "admin"]}><Settings /></Protected>} />
      <Route path="/dashboard" element={<Protected roles={["consumer"]}><ConsumerDashboard /></Protected>} />
      <Route path="/trees/new" element={<Protected roles={["consumer"]}><TreeCreate /></Protected>} />
      <Route path="/trees/:id" element={<Protected roles={["consumer"]}><TreeDetail /></Protected>} />
      <Route path="/provider" element={<Protected roles={["provider", "admin"]}><ProviderDashboard /></Protected>} />
      <Route path="/admin" element={<AdminProtected><AdminDashboard /></AdminProtected>} />
      <Route path="/change-password" element={<Protected roles={["consumer", "provider", "admin"]}><ChangePassword /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <AuthProvider>
        <BrowserRouter>
          <RoutedApp />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
